import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import Button from '@carrier-io/air-react/Button';
import Stack from '@carrier-io/air-react/Stack';
import { ConnectionContextType } from '../Connection';
import ButtonBase from '@carrier-io/air-react/ButtonBase';
import Typography from '@carrier-io/air-react/Typography';
import { Align, TypographyColor, Variant } from 'constants/componentEnums';
import Divider from '@carrier-io/air-react/Divider';
import CopyToClipboard from './CopyToClipboard';
import RefreshIcon from 'components/icons/RefreshIcon';
import CertificateStatus from './CertificateStatus';
import { config } from '../../../config';
import { useNotification } from 'context/NotificationContext';
import { getDataContractName } from 'utils/getDataContractName';
import { isIdValid } from 'utils/validators';
import { getNetworkStatusMessage } from 'utils/getNetworkStatusMessage';
import {
  isCertificateConnected,
  isProjectOngoing,
  isUploadingDisabled,
  ProjectOnboardingStatus,
  SORT_BY,
  UpdatedProjectSchema,
  VendorDataContractErrorList,
} from 'store/models/Project';
import { NotificationBgColor } from 'store/models/Notification';
import { GET_ERROR_MESSAGES_FOR_PROJECT } from 'store/graphql/queries/GET_ERROR_MESSAGES_FOR_PROJECT';
import { GET_COUNT_OF_REGISTERED_DEVICES } from 'store/graphql/queries/GET_COUNT_OF_REGISTERED_DEVICES';
import { UPDATE_PROJECT } from 'store/graphql/mutations/UPDATE_PROJECT';
import styles from '../../../styles/Connection.module.css';

const TestPayload = () => {
  const [dbirthErrorMessages, setDbirthErrorMessages] = useState<VendorDataContractErrorList[] | null>(null);
  const [ddataErrorMessages, setDdataErrorMessages] = useState<VendorDataContractErrorList[] | null>(null);
  const { handleChange, cancelConfigure, refetchVendorData, vendorData } = useOutletContext<ConnectionContextType>();
  const { projectId = '' } = useParams();
  const formattedProjectId = projectId?.toLocaleLowerCase();
  const currentProject = vendorData?.projects?.find((project) => project.id === formattedProjectId);
  const dataContractName = getDataContractName(vendorData, currentProject);
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const projectStatus: ProjectOnboardingStatus = currentProject?.status ?? ProjectOnboardingStatus.DRAFT;
  const isStatusConnected = isCertificateConnected(projectStatus);
  const isCertificateAlreadyUploaded = isProjectOngoing(projectStatus);
  const disableUpload = isUploadingDisabled(projectStatus);
  const [updateProject, { loading: updatingLoading }] = useMutation(UPDATE_PROJECT);

  const {
    loading: ddataDevicesStatsLoading,
    data: ddataDevicesStatsData,
    refetch: ddataDevicesRefetch,
    networkStatus: ddataDevicesNetworkStatus,
  } = useQuery(GET_COUNT_OF_REGISTERED_DEVICES, {
    variables: { dataContractName: dataContractName, fuzzySearch: false, sortBy: SORT_BY.DDATA },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !dataContractName,
  });

  const {
    loading: dbirthDevicesStatsLoading,
    data: dbirthDevicesStatsData,
    refetch: dbirthDevicesRefetch,
    networkStatus: dbirthDevicesNetworkStatus,
  } = useQuery(GET_COUNT_OF_REGISTERED_DEVICES, {
    variables: { dataContractName: dataContractName, fuzzySearch: false, sortBy: SORT_BY.DBIRTH },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !dataContractName,
  });

  const {
    data: dbirthErrorsData,
    loading: dbirthErrorsLoading,
    refetch: dbirthErrorsRefetch,
    networkStatus: dbirthErrorsNetworkStatus,
  } = useQuery(GET_ERROR_MESSAGES_FOR_PROJECT, {
    variables: {
      input: {
        dataContractName: dataContractName,
        fuzzySearch: false,
        sortBy: SORT_BY.DBIRTH,
      },
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !dataContractName,
  });
  const {
    data: ddataErrorsData,
    loading: ddataErrorsLoading,
    refetch: ddataErrorsRefetch,
    networkStatus: ddataErrorsNetworkStatus,
  } = useQuery(GET_ERROR_MESSAGES_FOR_PROJECT, {
    variables: {
      input: {
        dataContractName: dataContractName,
        fuzzySearch: false,
        sortBy: SORT_BY.DDATA,
      },
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !dataContractName,
  });

  const isValidUpdatedProject = ({ vendorId, projectId }: UpdatedProjectSchema) => {
    let errorMessage = '';
    let isValid = false;
    const isIdsValid = isIdValid(vendorId) && isIdValid(projectId);

    if (!isStatusConnected) {
      errorMessage = 'You should connect certificate before next step';
    } else if (!isIdsValid) {
      errorMessage = 'Invalid payload data';
    } else if (isIdsValid && isStatusConnected) {
      isValid = true;
    }

    return { errorMessage, isValid };
  };

  const handleUpdateProject = async () => {
    const updatedProjectData: UpdatedProjectSchema = {
      vendorId: vendorData?.vendorId ?? '',
      projectId: currentProject?.id ?? '',
      project: {
        status: ProjectOnboardingStatus.TESTED,
      },
    };

    const { isValid, errorMessage } = isValidUpdatedProject(updatedProjectData);

    if (isValid) {
      try {
        const response = await updateProject({
          variables: updatedProjectData,
        });
        console.log('Update Project Response: ', response.data);
        setTextContent('The project has been successfully tested');
        setNotificationBgColor(NotificationBgColor.SUCCESS);
        setIsNotificationOpened(true);
        refetchVendorData();
        handleChange(3, 3);
        cancelConfigure();
      } catch (err) {
        setTextContent('There was an issue with your request');
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);
        console.error('Update Project Status Error: ', err);
      }
    } else {
      setTextContent(errorMessage);
      setNotificationBgColor(NotificationBgColor.WARNING);
      setIsNotificationOpened(true);
    }
  };

  const handleSubmit = () => {
    if (isStatusConnected) {
      handleUpdateProject();
    }
  };

  const getDisplayText = (status: NetworkStatus, loading: boolean, count: number | undefined): string | number => {
    switch (status) {
      case NetworkStatus.refetch:
        return 'Refetching...';
      case NetworkStatus.loading:
        return 'Loading...';
      default:
        return !loading ? count ?? 0 : 0;
    }
  };

  useEffect(() => {
    if (!ddataErrorsLoading && ddataErrorsData) {
      setDdataErrorMessages(ddataErrorsData?.getErrorMessagesForProject?.vendorDataContractErrorList);
    }
  }, [ddataErrorsData]);

  useEffect(() => {
    if (!dbirthErrorsLoading && dbirthErrorsData) {
      setDbirthErrorMessages(dbirthErrorsData?.getErrorMessagesForProject?.vendorDataContractErrorList);
    }
  }, [dbirthErrorsData]);

  return (
    <Stack direction="column" spacing={2} width="80%">
      <Typography
        color={TypographyColor.Text.Primary}
        sx={{
          marginTop: '20px',
          marginBottom: '20px',
        }}
        variant={Variant.H4}
        align={Align.Left}
      >
        Test a Payload
      </Typography>
      <Divider variant="full" />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Configure Payloads
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Feel free to modify the sample <b>DBIRTH</b> payload at <b>./data/dbirth/dbirth-payload.json</b> and the sample{' '}
        <b>DDATA</b> payload at <b>./data/dbirth/ddata-payload.json</b>. Alternatively, you could find sample files in{' '}
        <b>./data</b> subfolders that corresponds to you device type <b>{currentProject?.type}</b>
      </Typography>
      <Typography
        color={TypographyColor.Text.Primary}
        variant={Variant.H6}
        sx={{
          marginTop: '20px',
        }}
        align={Align.Left}
      >
        Test DBIRTH publishing
      </Typography>
      <CopyToClipboard text={'npm run test-payload-dbirth'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        You should see output similar to the following:
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        {'Starting dbirth-payload command' +
          '\nPath to DBIRTH payload: ./data/dbirth/dbirth-payload.json' +
          '\nDBIRTH payload: { metrics: [ {' +
          '\n      "timestamp": "1740471112"' +
          '\n    }]' +
          '\n}' +
          '\nBorker URL: ' +
          config.mqttURL +
          '\nPath to CA certificate: ./ca/AmazonRootCA1.pem' +
          '\nPath to device certificate: ./ca/deviceCertAndCACert.crt' +
          '\nPath to device key: ./ca/deviceCert.key' +
          "\nCommand's execution status: dbirth-payload command is started" +
          '\n****Connected to Broker' +
          '\n****Start Publishing to: spBv1.0/Demo24032025/DBIRTH/test' +
          '\n****Publishing DBIRTH is successfull, result: OK.✔' +
          '\nPlease check debug log on NVIEW web application'}
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Watch for your test message below:
      </Typography>
      <Stack direction="row" spacing={2} width="100%" alignItems="center">
        <Typography
          className={styles.configField}
          color={TypographyColor.Text.Primary}
          variant={Variant.Body2}
          align={Align.Left}
        >
          {getDisplayText(
            dbirthDevicesNetworkStatus,
            dbirthDevicesStatsLoading,
            dbirthDevicesStatsData?.getCountOfRegisteredDevices?.count,
          )}
        </Typography>
        <Button
          data-testid="dbirth-devices-refetch"
          startIcon={<RefreshIcon />}
          color="primary"
          fullWidth={false}
          onClick={() => dbirthDevicesRefetch()}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        or, if errors occur, review them here:
      </Typography>
      <Stack direction="row" spacing={2} width="100%" alignItems="center">
        <Typography
          className={styles.configField}
          color={TypographyColor.Text.Primary}
          variant={Variant.Body2}
          align={Align.Left}
        >
          {getNetworkStatusMessage(
            dbirthErrorsNetworkStatus,
            dbirthErrorsLoading,
            dbirthErrorMessages?.length ?? 0,
            dbirthErrorMessages,
          )}
        </Typography>
        <Button
          data-testid="dbirth-errors-refetch"
          startIcon={<RefreshIcon />}
          color="primary"
          fullWidth={false}
          onClick={() => dbirthErrorsRefetch()}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Test DDATA publishing
      </Typography>
      <CopyToClipboard text={'npm run test-payload-ddata'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        You should see output similar to the following:
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        {'Starting ddata-payload command' +
          '\nPath to DDATA payload: ./data/ddata/ddata-payload.json' +
          '\nDDATA payload: { metrics: [ {' +
          '\n      "timestamp": "1740471112"' +
          '\n    }]' +
          '\n}' +
          '\nBorker URL: ' +
          config.mqttURL +
          '\nPath to CA certificate: ./ca/AmazonRootCA1.pem' +
          '\nPath to device certificate: ./ca/deviceCertAndCACert.crt' +
          '\nPath to device key: ./ca/deviceCert.key' +
          "\nCommand's execution status: ddata-payload command is started" +
          '\n****Connected to Broker' +
          '\n****Start Publishing to: spBv1.0/Demo24032025/DDATA/test' +
          '\n****Publishing DDATA is successfull, result: OK.✔' +
          '\nPlease check debug log on NVIEW web application'}
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Count of registered devices. As usual it should go from 0 to 1 in 2-3 minutes:
      </Typography>
      <Stack direction="row" spacing={2} width="100%" alignItems="center">
        <Typography
          className={styles.configField}
          color={TypographyColor.Text.Primary}
          variant={Variant.Body2}
          align={Align.Left}
        >
          {getDisplayText(
            ddataDevicesNetworkStatus,
            ddataDevicesStatsLoading,
            ddataDevicesStatsData?.getCountOfRegisteredDevices?.count,
          )}
        </Typography>
        <Button
          data-testid="ddata-devices-refetch"
          startIcon={<RefreshIcon />}
          color="primary"
          fullWidth={false}
          onClick={() => ddataDevicesRefetch()}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        or, if errors occur, review them here:
      </Typography>
      <Stack direction="row" spacing={2} width="100%" alignItems="center">
        <Typography
          className={styles.configField}
          color={TypographyColor.Text.Primary}
          variant={Variant.Body2}
          align={Align.Left}
        >
          {getNetworkStatusMessage(
            ddataErrorsNetworkStatus,
            ddataErrorsLoading,
            ddataErrorMessages?.length ?? 0,
            ddataErrorMessages,
          )}
        </Typography>
        <Button
          data-testid="ddata-errors-refetch"
          startIcon={<RefreshIcon />}
          color="primary"
          fullWidth={false}
          onClick={() => ddataErrorsRefetch()}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        If you observe errors, please ensure that your internet connection is fine and go back to the previuos step to
        verify test application configuration. If none helps, please contact Carrier support.
      </Typography>
      {(isCertificateAlreadyUploaded || disableUpload) && <CertificateStatus status={projectStatus} />}
      <Divider variant="full" />
      <Stack direction="row" spacing={2} alignSelf="flex-end">
        <ButtonBase data-testid="step-cancel" onClick={cancelConfigure}>
          <Typography color="primary">Cancel</Typography>
        </ButtonBase>
        <Button
          data-testid="finish-setup"
          disabled={!isStatusConnected || updatingLoading}
          color="primary"
          fullWidth={false}
          onClick={handleSubmit}
        >
          Finish Setup
        </Button>
      </Stack>
    </Stack>
  );
};

export default TestPayload;
